import { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { EIconSize } from 'utils/constants/app/ui';
import { isMobile } from 'utils/is-mobile';
import { ResultsGame } from 'interfaces/results';
import { SingleGame } from './SingleGame';
import {
  GamesSkeletonDesktop,
  OpenedGameSkeletonDesktop
} from './SkeletonDesktop';
import {
  GameSingleItemSkeletonMobile,
  GamesSkeletonMobile
} from './SkeletonMobile';
import { EmptyResults } from 'components/EmptyResultBoxes/EmptyResults';
import { EventCollapse } from 'components/EventCollapse';
import { GlobalIcon } from 'components/GlobalIcon';
import { LazyLoad } from 'components/Shared/LazyLoad';
import {
  getResultsDataLoading,
  getResultsTrigger
} from 'store/selectors/results-data';
import './index.less';

type Props = {
  games: ResultsGame[] | null;
  loading: boolean;
  gameId?: string;
};

export const Games: FC<Props> = ({ games, loading, gameId }) => {
  const triger = useSelector(getResultsTrigger);
  const resultsDataLoading = useSelector(getResultsDataLoading);
  const history = useHistory();
  const [groupedGames, setGroupedGames] = useState<Record<
    string,
    ResultsGame[]
  > | null>(null);

  const viewport = useRef(null);

  useEffect(() => {
    setGroupedGames(null);
  }, [triger]);

  useEffect(() => {
    if (games) {
      const result = games.reduce((r, a) => {
        r[a.competition_id] = r[a.competition_id] || [];
        r[a.competition_id].push(a);

        return r;
      }, Object.create(null));

      setGroupedGames(result);
    } else {
      setGroupedGames(null);
    }
  }, [games]);

  const isLoading = loading || !groupedGames || resultsDataLoading;

  return (
    <div className="results__games" ref={viewport}>
      {isLoading ? (
        isMobile() ? (
          gameId ? (
            <GameSingleItemSkeletonMobile expanded />
          ) : (
            <GamesSkeletonMobile />
          )
        ) : gameId ? (
          <OpenedGameSkeletonDesktop />
        ) : (
          <GamesSkeletonDesktop />
        )
      ) : !Object.keys(groupedGames).length ? (
        <EmptyResults
          className={isMobile() ? 'emptyResultsPage--mobile' : ''}
        />
      ) : (
        <LazyLoad
          customViewport={isMobile() ? undefined : viewport.current}
          initialCount={10}
          loadCount={10}
          data={Object.keys(groupedGames)}
          renderer={(competitionId: string, index: number) => (
            <EventCollapse
              wrapperClassName={
                isMobile() ? 'resultSingleGameWrapper--mobile' : ''
              }
              style={{ marginTop: index ? '4px' : 0 }}
              title={groupedGames[competitionId]?.[0]?.competition_name}
              key={`${index}-${competitionId}-${groupedGames[competitionId]?.[0]?.game_id}`}
              noMargin
              defaultActive
              icon={
                <GlobalIcon
                  lib="flags"
                  name={groupedGames[competitionId]?.[0]?.region_alias}
                  size={EIconSize._20}
                />
              }
            >
              <>
                {(groupedGames[competitionId] || []).map(game => (
                  <SingleGame
                    gameId={gameId}
                    game={game}
                    key={game.game_id}
                    history={history}
                  />
                ))}
              </>
            </EventCollapse>
          )}
        />
      )}
    </div>
  );
};
